/**
 * Traffic Channel Classification
 *
 * Categorizes visitor traffic into granular channels based on UTM parameters
 * and referrer information. This enables restaurants to understand where their
 * website visitors are coming from and measure marketing effectiveness.
 */
import {
  getReferrerChannel,
  ReferrerChannel
} from 'src/lib/js/trackerUtilities'

export enum TrafficChannel {
  /** Typed URL, bookmark, internal navigation, or utm_source without utm_medium */
  DIRECT = 'direct',
  /** Unpaid search engine traffic (Google, Bing, Yahoo, etc.) */
  ORGANIC_SEARCH = 'organic_search',
  /** Paid search ads (utm_medium=cpc/ppc/paidsearch with search engine source) */
  PAID_SEARCH = 'paid_search',
  /** Unpaid social media traffic (Facebook, Instagram, Twitter, LinkedIn, TikTok, etc.) */
  ORGANIC_SOCIAL = 'organic_social',
  /** Paid social media ads (utm_medium=cpc/paidsocial with social platform source) */
  PAID_SOCIAL = 'paid_social',
  /** External website links without UTM parameters (blogs, news, partners, directories, URL shorteners) or utm_medium=referral */
  REFERRAL = 'referral',
  /** Email campaigns (utm_medium=email/newsletter or utm_source indicating email) */
  EMAIL = 'email',
  /** SMS/text message campaigns (utm_medium=sms/text) */
  SMS = 'sms',
  /** Display/banner advertising (utm_medium=display/banner/cpm/retargeting/remarketing) */
  PAID_DISPLAY = 'paid_display',
  /** Paid traffic not classified as Search/Social/Display (utm_medium contains paid/cpc/ppc) */
  OTHER_PAID = 'other_paid',
  /** AI assistants and chatbots (ChatGPT, Claude, Gemini, Perplexity, Copilot, etc.) */
  AI = 'ai'
}

export interface TrafficSourceData {
  trafficChannel: TrafficChannel
  trafficSource?: string
  referrer?: string
  referringDomain?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
}

const PLATFORM_SOURCES = {
  search: [
    'google',
    'bing',
    'yahoo',
    'duckduckgo',
    'baidu',
    'yandex',
    'ask',
    'aol',
    'ecosia',
    'qwant'
  ],
  social: [
    'facebook',
    'twitter',
    'instagram',
    'linkedin',
    'pinterest',
    'reddit',
    'tiktok',
    'snapchat',
    'youtube',
    'tumblr',
    'whatsapp',
    'telegram',
    't.co', // Twitter short links
    'fb.me', // Facebook short links
    'lnkd.in' // LinkedIn short links
  ],
  ai: [
    'chat.openai.com', // ChatGPT
    'chatgpt.com', // ChatGPT alternate domain
    'openai.com', // OpenAI
    'gemini.google.com', // Google Gemini
    'bard.google.com', // Google Bard (legacy)
    'claude.ai', // Claude
    'perplexity.ai', // Perplexity
    'you.com', // You.com AI search
    'poe.com', // Poe (Quora's AI)
    'character.ai', // Character.AI
    'copilot.microsoft.com', // Microsoft Copilot
    'mistral.ai', // Mistral AI
    'inflection.ai', // Inflection AI (Pi)
    'anthropic.com' // Anthropic
  ]
}

// Mapping of AI platform domains to their simple names
const AI_PLATFORM_NAMES: Record<string, string> = {
  'chat.openai.com': 'openai',
  'chatgpt.com': 'openai',
  'openai.com': 'openai',
  'gemini.google.com': 'gemini',
  'bard.google.com': 'gemini',
  'claude.ai': 'claude',
  'anthropic.com': 'claude',
  'perplexity.ai': 'perplexity',
  'you.com': 'you',
  'poe.com': 'poe',
  'character.ai': 'character',
  'copilot.microsoft.com': 'copilot',
  'mistral.ai': 'mistral',
  'inflection.ai': 'inflection'
}

// Mapping of social short links to their canonical names
const SOCIAL_SHORT_LINKS: Record<string, string> = {
  't.co': 'twitter',
  'fb.me': 'facebook',
  'lnkd.in': 'linkedin'
}

// Paid ad click IDs. fbclid excluded — Facebook adds it to organic clicks too.
const PAID_CLICK_IDS: Array<{
  param: string
  source: string
  channel: TrafficChannel
}> = [
  { param: 'gclid', source: 'google', channel: TrafficChannel.PAID_SEARCH },
  { param: 'gbraid', source: 'google', channel: TrafficChannel.PAID_SEARCH },
  { param: 'wbraid', source: 'google', channel: TrafficChannel.PAID_SEARCH },
  { param: 'msclkid', source: 'bing', channel: TrafficChannel.PAID_SEARCH },
  { param: 'ttclid', source: 'tiktok', channel: TrafficChannel.PAID_SOCIAL },
  { param: 'twclid', source: 'twitter', channel: TrafficChannel.PAID_SOCIAL },
  {
    param: 'li_fat_id',
    source: 'linkedin',
    channel: TrafficChannel.PAID_SOCIAL
  }
]

function detectPaidClickId(url: string) {
  try {
    const params = new URL(url).searchParams
    return PAID_CLICK_IDS.find(({ param }) => params.has(param))
  } catch {
    return undefined
  }
}

// 'Google Ad' and 'SEO' omitted — gclid/google-referrer logic covers them.
const TOAST_REFERRER_OVERRIDES: Partial<
  Record<ReferrerChannel, { channel?: TrafficChannel; source: string }>
> = {
  OWG: { channel: TrafficChannel.ORGANIC_SEARCH, source: 'owg' },
  'Ask Maps': { channel: TrafficChannel.ORGANIC_SEARCH, source: 'ask_maps' },
  'Voice Ordering': { source: 'voice_ordering' },
  Flyer: { source: 'flyer' },
  '3PO QR Code': { source: '3po_qr_code' }
}

function applyToastReferrerOverride(
  result: TrafficSourceData,
  referrer: string,
  currentUrl: string
): TrafficSourceData {
  const referrerChannel = getReferrerChannel(currentUrl, referrer)
  const override = referrerChannel
    ? TOAST_REFERRER_OVERRIDES[referrerChannel]
    : undefined

  if (override) {
    result.trafficSource = override.source
    if (override.channel) {
      result.trafficChannel = override.channel
    }
  }

  return result
}

function extractUTMParams(url: string) {
  try {
    const urlObj = new URL(url)
    return {
      utmSource: urlObj.searchParams.get('utm_source') || undefined,
      utmMedium: urlObj.searchParams.get('utm_medium') || undefined,
      utmCampaign: urlObj.searchParams.get('utm_campaign') || undefined,
      utmTerm: urlObj.searchParams.get('utm_term') || undefined,
      utmContent: urlObj.searchParams.get('utm_content') || undefined
    }
  } catch {
    return {}
  }
}

function extractDomain(url: string): string | undefined {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname
  } catch {
    return undefined
  }
}

// Loose matching for free-form utm_source values like 'googleads'.
function isSearchEngineSource(source: string): boolean {
  const lowerSource = source.toLowerCase()
  return PLATFORM_SOURCES.search.some((engine) => lowerSource.includes(engine))
}

function isSocialPlatformSource(source: string): boolean {
  const lowerSource = source.toLowerCase()
  return PLATFORM_SOURCES.social.some((platform) =>
    lowerSource.includes(platform)
  )
}

// Subdomains match, substring look-alikes (teasnyou.com vs you.com) don't.
function matchesPlatformDomain(hostname: string, platform: string): boolean {
  if (!platform.includes('.')) {
    return hostname.split('.').includes(platform)
  }
  return hostname === platform || hostname.endsWith('.' + platform)
}

function isSearchEngineDomain(domain: string): boolean {
  const lowerDomain = domain.toLowerCase()
  return PLATFORM_SOURCES.search.some((engine) =>
    matchesPlatformDomain(lowerDomain, engine)
  )
}

function isSocialPlatformDomain(domain: string): boolean {
  const lowerDomain = domain.toLowerCase()
  return PLATFORM_SOURCES.social.some((platform) =>
    matchesPlatformDomain(lowerDomain, platform)
  )
}

function isAIPlatform(domain: string): boolean {
  const lowerDomain = domain.toLowerCase()
  return PLATFORM_SOURCES.ai.some((platform) =>
    matchesPlatformDomain(lowerDomain, platform)
  )
}

function isPaidMedium(medium: string): boolean {
  const lowerMedium = medium.toLowerCase()
  return (
    lowerMedium.includes('cp') || // GA4 .*cp.* pattern (matches cpc, cpm, acpc)
    lowerMedium.includes('ppc') || // ppc doesn't contain 'cp' substring
    lowerMedium.startsWith('paid') || // Prevents "unpaid" false positives
    lowerMedium.includes('retargeting')
  )
}

/**
 * Extracts the specific traffic source name from a domain.
 * Returns the platform name (e.g., "facebook", "google", "claude")
 */
function extractTrafficSource(domain: string): string | undefined {
  if (!domain) return undefined

  const lowerDomain = domain.toLowerCase()

  for (const aiDomain of PLATFORM_SOURCES.ai) {
    if (matchesPlatformDomain(lowerDomain, aiDomain)) {
      return AI_PLATFORM_NAMES[aiDomain] || aiDomain
    }
  }

  for (const engine of PLATFORM_SOURCES.search) {
    if (matchesPlatformDomain(lowerDomain, engine)) {
      return engine
    }
  }

  for (const platform of PLATFORM_SOURCES.social) {
    if (matchesPlatformDomain(lowerDomain, platform)) {
      return SOCIAL_SHORT_LINKS[platform] || platform
    }
  }

  return undefined
}

function isEmailSource(source: string): boolean {
  const lowerSource = source.toLowerCase()
  return (
    lowerSource.includes('email') ||
    lowerSource.includes('e-mail') ||
    lowerSource.includes('e_mail')
  )
}

function categorizeByUTMMedium(
  medium: string,
  source?: string
): TrafficChannel | null {
  const lowerMedium = medium.toLowerCase()
  const lowerSource = source?.toLowerCase()

  // AI must be checked before paid to avoid "paid" matching "ai"
  if (
    lowerMedium === 'ai' ||
    lowerMedium === 'chatbot' ||
    lowerMedium.startsWith('ai_') ||
    lowerMedium.endsWith('_ai') ||
    lowerMedium.includes('_ai_')
  ) {
    return TrafficChannel.AI
  }

  if (
    lowerMedium.includes('email') ||
    lowerMedium.includes('e-mail') ||
    lowerMedium.includes('e_mail') ||
    lowerMedium === 'newsletter' ||
    (lowerSource &&
      (lowerSource.includes('email') ||
        lowerSource.includes('e-mail') ||
        lowerSource.includes('e_mail')))
  ) {
    return TrafficChannel.EMAIL
  }

  if (lowerMedium.includes('sms') || lowerMedium === 'text') {
    return TrafficChannel.SMS
  }

  if (
    lowerMedium === 'paidsearch' ||
    (isPaidMedium(lowerMedium) &&
      (lowerMedium.includes('search') ||
        (lowerSource && isSearchEngineSource(lowerSource))))
  ) {
    return TrafficChannel.PAID_SEARCH
  }

  if (
    lowerMedium === 'paidsocial' ||
    (isPaidMedium(lowerMedium) &&
      (lowerMedium.includes('social') ||
        (lowerSource && isSocialPlatformSource(lowerSource))))
  ) {
    return TrafficChannel.PAID_SOCIAL
  }

  if (
    lowerMedium.includes('display') ||
    lowerMedium.includes('banner') ||
    lowerMedium === 'cpm' ||
    lowerMedium === 'expandable' ||
    lowerMedium === 'interstitial' ||
    lowerMedium === 'retargeting' ||
    lowerMedium === 'remarketing'
  ) {
    return TrafficChannel.PAID_DISPLAY
  }

  if (lowerMedium.includes('organic') || lowerMedium.includes('search')) {
    return TrafficChannel.ORGANIC_SEARCH
  }

  if (lowerMedium.includes('social')) {
    return TrafficChannel.ORGANIC_SOCIAL
  }

  if (lowerMedium.includes('referral')) {
    return TrafficChannel.REFERRAL
  }

  if (isPaidMedium(lowerMedium)) {
    return TrafficChannel.OTHER_PAID
  }

  return null
}

/**
 * Categorizes known platforms (AI, search, social).
 * Returns null for unknown domains.
 */
function categorizeKnownPlatforms(domain: string): TrafficChannel | null {
  if (isAIPlatform(domain)) {
    return TrafficChannel.AI
  }

  if (isSearchEngineDomain(domain)) {
    return TrafficChannel.ORGANIC_SEARCH
  }

  if (isSocialPlatformDomain(domain)) {
    return TrafficChannel.ORGANIC_SOCIAL
  }

  return null
}

function isSameDomain(referrer: string, currentUrl: string): boolean {
  const referrerDomain = extractDomain(referrer)
  const currentDomain = extractDomain(currentUrl)

  return (
    referrerDomain !== undefined &&
    currentDomain !== undefined &&
    referrerDomain === currentDomain
  )
}

/**
 * Detects and classifies traffic source.
 *
 * Classification priority:
 * 1. UTM source indicating email (without UTM medium) → EMAIL
 * 2. UTM medium (if present) → classified by medium (most specific intent signal)
 * 3. Paid click ID in landing URL (gclid, msclkid, etc.) → PAID_SEARCH / PAID_SOCIAL
 * 4. Referrer domain (if present)
 *    - Same domain (internal navigation) → DIRECT
 *    - Known platforms (AI, search, social) → classified accordingly
 *    - Cross-domain (external websites) → REFERRAL
 * 5. No signals → DIRECT
 *
 * Toast referrer signals (OWG, flyers, etc.) are applied last and may override.
 */
export function detectTrafficSource(
  referrer: string,
  currentUrl: string
): TrafficSourceData {
  return applyToastReferrerOverride(
    classifyTrafficSource(referrer, currentUrl),
    referrer,
    currentUrl
  )
}

function classifyTrafficSource(
  referrer: string,
  currentUrl: string
): TrafficSourceData {
  const utmParams = extractUTMParams(currentUrl)

  const result: TrafficSourceData = {
    trafficChannel: TrafficChannel.DIRECT,
    ...utmParams
  }

  if (referrer) {
    result.referrer = referrer
    result.referringDomain = extractDomain(referrer)
  }

  // Set traffic source: prioritize utm_source, fallback to extracted domain source
  if (utmParams.utmSource) {
    result.trafficSource = utmParams.utmSource.toLowerCase()

    // Check if source indicates email (when no medium is present)
    if (!utmParams.utmMedium && isEmailSource(utmParams.utmSource)) {
      result.trafficChannel = TrafficChannel.EMAIL
      return result
    }
  } else if (result.referringDomain) {
    result.trafficSource = extractTrafficSource(result.referringDomain)
  }

  if (utmParams.utmMedium) {
    const channelFromMedium = categorizeByUTMMedium(
      utmParams.utmMedium,
      utmParams.utmSource
    )
    if (channelFromMedium) {
      result.trafficChannel = channelFromMedium
      return result
    }
  }

  const paidClickId = detectPaidClickId(currentUrl)
  if (paidClickId) {
    result.trafficChannel = paidClickId.channel
    if (!result.trafficSource) {
      result.trafficSource = paidClickId.source
    }
    return result
  }

  if (result.referringDomain) {
    if (isSameDomain(referrer, currentUrl)) {
      // Internal navigation is not a traffic acquisition channel - classify as DIRECT
      result.trafficChannel = TrafficChannel.DIRECT
      return result
    }

    // Check if it's a known platform (AI, search, social)
    const platformChannel = categorizeKnownPlatforms(result.referringDomain)
    if (platformChannel) {
      result.trafficChannel = platformChannel
      return result
    }

    // Cross-domain traffic from external websites without UTM parameters is REFERRAL
    result.trafficChannel = TrafficChannel.REFERRAL
    return result
  }

  result.trafficChannel = TrafficChannel.DIRECT
  return result
}
